import {
    computed,
    type ComputedRef,
    type MaybeRef,
    onMounted,
    type Ref,
    ref,
    unref,
    watch,
    type WritableComputedRef,
} from 'vue'
import { useEventListener, useWindowSize } from '@vueuse/core'
import { getRequestAnimationFrame } from '@/helpers/globals'

export type AnimationFrameFunction = (
    item: HTMLElement, // apply transforms to this element
    itemParent: HTMLElement, // use to get item measurments (width, position)
    container: HTMLElement // scroll container
) => void

// ensure correct SSR state
const initialSlideWidth = 20

interface Carousel {
    container: Ref<HTMLElement | null>
    elements: Ref<HTMLElement[]>
    itemsAnimated: Ref<HTMLElement[]>
    current: WritableComputedRef<number, number>
    scrollPercent: ComputedRef<number>
    isAtStart: ComputedRef<boolean>
    isAtEnd: ComputedRef<boolean>
    previous: () => void
    next: () => void
    slideWidth: Ref<number>
    containerScrollLeft: Ref<number>
    containerOffsetWidth: Ref<number>
}

export function useCarousel(
    items: Ref<unknown[]>, // used to calculate correct SSR state
    isCentered: MaybeRef<boolean> = false,
    animation?: AnimationFrameFunction,
    behavior: 'smooth' | 'instant' = 'smooth',
    initial?: number
): Carousel {
    const container: Ref<HTMLElement | null> = ref(null)
    const elements = ref<HTMLElement[]>([]) as Ref<HTMLElement[]>
    const slideWidth = ref(initialSlideWidth)
    const containerScrollLeft = ref(0)

    useEventListener(
        container,
        'scroll',
        (e) => {
            containerScrollLeft.value = (e.target as HTMLElement).scrollLeft
        },
        {
            passive: true,
        }
    )

    const scrollTo = (x: number) => {
        container.value?.scrollTo({
            left: x,
            behavior,
        })
    }
    const containerOffsetWidth = ref(initialSlideWidth)
    const updateMeasurments = () => {
        if (elements.value.length && container.value) {
            slideWidth.value = Math.floor(
                elements.value[0]?.getBoundingClientRect().width || 0
            )

            containerOffsetWidth.value = Math.floor(
                container.value.getBoundingClientRect().width
            )
        }
    }

    onMounted(updateMeasurments)

    if (!import.meta.env.SSR) {
        const { width } = useWindowSize()

        watch(width, updateMeasurments, { immediate: true })
    }

    function calculateContainerScrollLeft(
        index: number,
        isCentered: boolean
    ): number {
        if (isCentered) {
            return (
                index * slideWidth.value -
                containerOffsetWidth.value / 2 +
                slideWidth.value / 2
            )
        }

        return index * slideWidth.value
    }

    function calculateCurrent(isCentered: boolean): number {
        if (isCentered) {
            return Math.round(
                (containerScrollLeft.value +
                    containerOffsetWidth.value / 2 -
                    slideWidth.value / 2) /
                    slideWidth.value
            )
        }

        return Math.round(containerScrollLeft.value / slideWidth.value)
    }

    const hasRanMounted = ref(false)
    onMounted(() => {
        if (typeof initial !== 'undefined') {
            container.value?.scrollTo({
                left: calculateContainerScrollLeft(initial, unref(isCentered)),
                behavior: 'instant',
            })
            hasRanMounted.value = true
        }
    })

    const current = computed({
        get() {
            if (typeof initial !== 'undefined' && !hasRanMounted.value) {
                // This prevents current from being 0, 1, 2, 3, etc. when initializing
                // Without this, carousel dots will flicker through to the initial image
                return initial
            }

            return calculateCurrent(unref(isCentered)) || 0
        },
        set(index: number) {
            scrollTo(calculateContainerScrollLeft(index, unref(isCentered)))
        },
    })

    const isAtStart = computed(() => current.value <= 0)
    const isAtEnd = computed(
        () =>
            current.value >=
            items.value.length -
                Math.floor(containerOffsetWidth.value / slideWidth.value)
    )

    const previous = () => {
        current.value -= 1
    }

    const next = () => {
        current.value += 1
    }

    const scrollPercent = computed(
        () => containerScrollLeft.value / containerOffsetWidth.value
    )

    const requestAnimationFrame = getRequestAnimationFrame()
    const itemsAnimated = ref<HTMLElement[]>([]) as Ref<HTMLElement[]>
    const animate = () => {
        if (!container.value || !animation) {
            return
        }

        for (let i = 0; i < elements.value.length; i += 1) {
            const animetedElement = itemsAnimated.value[i]
            const element = elements.value[i]

            if (!animetedElement || !element) {
                return
            }

            animation(animetedElement, element, container.value)
        }

        requestAnimationFrame(animate)
    }

    if (animation && !import.meta.env.SSR) {
        requestAnimationFrame(animate)
    }

    return {
        container,
        elements,
        itemsAnimated,
        current,
        scrollPercent,
        isAtStart,
        isAtEnd,
        previous,
        next,
        slideWidth,
        containerScrollLeft,
        containerOffsetWidth,
    }
}
